import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { AlertTriangle, Check, CheckCheck, Star, Undo2, X } from "lucide-react"

import { api } from "../api"
import { ConfirmDialog, EmptyState, formatMonthYear, LoadingCard, messageFor, Page } from "../shared"
import { fa, type TasksResponse } from "../process"
import { ReturnDialog } from "@/components/return-dialog"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Field, FieldError, FieldLabel } from "@/components/ui/field"
import { Textarea } from "@/components/ui/textarea"

type Split = Array<{ businessUnitId: number; percentage: string; businessUnit: { id: number; nameFa: string } }>
type DivisionItem = { id: number; jalaliYear: number; jalaliMonth: number; notes: string; employee: { nameFa: string; division?: { nameFa: string } | null }; lines: Split }
type UnitItem = { id: number; percentage: string; businessUnit: { id: number; nameFa: string }; allocation: { id: number; jalaliYear: number; jalaliMonth: number; notes: string; employee: { nameFa: string }; lines: Split } }
type Row = { actionId: number; allocationId: number; year: number; month: number; employee: string; subtitle: string; notes: string; lines: Split; focusUnitId?: number }
type ApprovedItem = { id: number; jalaliYear: number; jalaliMonth: number; status: string; employee: { nameFa: string; division?: { nameFa: string } | null }; lines: Split }

/** Division heads approve whole splits; business heads approve their unit's share of each split. */
export function ApprovalPage() {
  const { kind } = useParams()
  const division = kind === "divisions"
  const path = division ? "/approvals/divisions" : "/approvals/business-units"
  const client = useQueryClient()
  const queue = useQuery({ queryKey: ["approvals", kind], queryFn: () => api<Array<DivisionItem | UnitItem>>(path) })
  const tasks = useQuery({ queryKey: ["process-tasks"], queryFn: () => api<TasksResponse>("/process/tasks") })
  const [rejecting, setRejecting] = useState<Row | null>(null)
  const [bulkOpen, setBulkOpen] = useState<Row[] | null>(null)
  const [busy, setBusy] = useState<number | "all" | null>(null)
  const [error, setError] = useState("")

  const rows: Row[] = useMemo(() => (queue.data ?? []).map((raw) => {
    if (division) {
      const item = raw as DivisionItem
      return { actionId: item.id, allocationId: item.id, year: item.jalaliYear, month: item.jalaliMonth, employee: item.employee.nameFa, subtitle: item.employee.division?.nameFa ?? "", notes: item.notes, lines: item.lines }
    }
    const item = raw as UnitItem
    return { actionId: item.id, allocationId: item.allocation.id, year: item.allocation.jalaliYear, month: item.allocation.jalaliMonth, employee: item.allocation.employee.nameFa, subtitle: `سهم ${item.businessUnit.nameFa}: ${pct(item.percentage)}`, notes: item.allocation.notes, lines: item.allocation.lines, focusUnitId: item.businessUnit.id }
  }), [queue.data, division])

  const groups = useMemo(() => {
    const map = new Map<string, Row[]>()
    for (const row of rows) map.set(`${row.year}-${row.month}`, [...(map.get(`${row.year}-${row.month}`) ?? []), row])
    return [...map.entries()]
  }, [rows])

  const refresh = () => Promise.all(["approvals", "returnable", "process-tasks"].map((key) => client.invalidateQueries({ queryKey: [key] })))
  const act = (id: number, verb: "approve" | "reject", reason = "") => api<void>(`${path}/${id}/${verb}`, { method: "POST", body: JSON.stringify({ reason }) })
  const approve = async (targets: Row[], marker: number | "all") => {
    setBusy(marker); setError("")
    try { for (const row of targets) await act(row.actionId, "approve") } catch (failure) { setError(messageFor(failure)) } finally { setBusy(null); await refresh() }
  }

  const deadline = tasks.data?.cycle.deadlines[division ? "division" : "unit"]
  return (
    <Page
      title={division ? "تأیید مدیر واحد" : "تأیید نهایی مدیر کسب‌وکار"}
      description={division ? "نسبت‌های کاری پرسنل واحد خود را بررسی کنید. پس از تأیید شما، هر سهم برای مدیر کسب‌وکار مربوط ارسال می‌شود." : "سهم کسب‌وکار خود از وقت هر نفر را بررسی و تأیید نهایی کنید."}
    >
      {deadline && rows.length > 0 && <p className={cn("flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-sm", deadline.passed ? "border-loss/40 bg-loss/5 font-medium text-loss" : "text-muted-foreground")}>{deadline.passed && <AlertTriangle className="size-4" aria-hidden />}{deadline.passed ? "مهلت تأیید گذشته است: " : "مهلت تأیید: "}{fa(deadline.label)}</p>}
      {error && <p role="alert" className="rounded-lg border border-loss/40 bg-loss/5 px-4 py-3 text-sm text-loss">{error}</p>}
      {queue.isLoading ? <LoadingCard /> : !rows.length ? <Card><CardContent><EmptyState done text="موردی منتظر تأیید شما نیست. موارد تازه همین‌جا و در صفحه کارهای من نمایش داده می‌شوند." /></CardContent></Card> : groups.map(([key, items]) => (
        <Card key={key}>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <div>
              <CardTitle className="text-base">{formatMonthYear(items[0]!.year, items[0]!.month)}</CardTitle>
              <CardDescription>{items.length.toLocaleString("fa-IR")} مورد منتظر تأیید</CardDescription>
            </div>
            {items.length > 1 && (
              <Button variant="outline" disabled={busy !== null} onClick={() => setBulkOpen(items)}>
                <CheckCheck className="size-4" aria-hidden /> {busy === "all" ? "در حال تأیید…" : "تأیید همه"}
              </Button>
            )}
          </CardHeader>
          <CardContent className="grid gap-3">
            {items.map((row) => (
              <div key={row.actionId} className="grid gap-3 rounded-lg border p-4 transition-colors hover:border-foreground/15 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <Link to={`/allocations/${row.allocationId}`} className="font-medium hover:underline">{row.employee}</Link>
                    <span className="text-sm text-muted-foreground">{row.subtitle}</span>
                    {division && <SumChip lines={row.lines} />}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {row.lines.map((line) => {
                      const focus = line.businessUnitId === row.focusUnitId
                      return (
                        <Badge key={line.businessUnitId} variant={focus ? "default" : "secondary"} className="gap-1 font-normal tabular-nums" aria-label={`${line.businessUnit.nameFa} ${pct(line.percentage)}${focus ? "، سهم شما" : ""}`}>
                          {focus && <Star className="size-3" aria-hidden />}
                          {line.businessUnit.nameFa} {pct(line.percentage)}
                          {focus && <span>(سهم شما)</span>}
                        </Badge>
                      )
                    })}
                  </div>
                  {row.notes && <p className="mt-2 text-xs text-muted-foreground">توضیح: {row.notes}</p>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" disabled={busy !== null} onClick={() => setRejecting(row)}><X className="size-4" /> برگشت</Button>
                  <Button disabled={busy !== null} onClick={() => void approve([row], row.actionId)}><Check className="size-4" /> {busy === row.actionId ? "در حال تأیید…" : "تأیید"}</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
      {bulkOpen && (
        <ConfirmDialog
          open onOpenChange={(next) => { if (!next) setBulkOpen(null) }}
          title={`تأیید همه (${fa(bulkOpen.length)} مورد)`}
          description={`همه ${bulkOpen.length.toLocaleString("fa-IR")} مورد تأیید شود؟\nدر صورت خطا، مواردِ انجام‌نشده باقی می‌مانند و خطا بالای فهرست نمایش داده می‌شود.`}
          confirmLabel="تأیید همه" pending={busy === "all"}
          onConfirm={() => { void approve(bulkOpen, "all").then(() => setBulkOpen(null)) }}
        >
          <ul className="grid max-h-56 gap-1 overflow-y-auto rounded-md border p-2">
            {bulkOpen.map((row) => (
              <li key={row.actionId} className="flex items-center justify-between gap-2 border-b py-1 text-sm last:border-0">
                <span className="font-medium">{row.employee}</span>
                <span className="text-muted-foreground">{row.subtitle}</span>
              </li>
            ))}
          </ul>
        </ConfirmDialog>
      )}
      {rejecting && <RejectDialog row={rejecting} onClose={() => setRejecting(null)} onReject={(reason) => act(rejecting.actionId, "reject", reason).then(async () => { setRejecting(null); await refresh() })} />}
      {kind && <ApprovedList kind={kind} />}
    </Page>
  )
}

/** Recently approved splits this person can still send back to their owner for correction. */
function ApprovedList({ kind }: { kind: string }) {
  const approved = useQuery({ queryKey: ["returnable", kind], queryFn: () => api<ApprovedItem[]>(`/approvals/returnable/${kind}`) })
  const [target, setTarget] = useState<ApprovedItem | null>(null)
  if (!approved.data?.length) return null
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">تأییدشده‌ها</CardTitle>
        <CardDescription>اگر نسبت کاری‌ای اشتباه تأیید شده یا فرد اشتباه وارد کرده، تا وقتی دوره‌ی ماه بسته نشده می‌توانید آن را برای اصلاح به کارتابل او برگردانید.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-2">
        {approved.data.map((item) => (
          <div key={item.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2">
                <Link to={`/allocations/${item.id}`} className="font-medium hover:underline">{item.employee.nameFa}</Link>
                <span className="text-sm text-muted-foreground">{formatMonthYear(item.jalaliYear, item.jalaliMonth)}</span>
                <Badge variant="secondary" className="font-normal">{item.status === "approved" ? "تأیید نهایی" : "منتظر تأیید کسب‌وکارها"}</Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground tabular-nums">{item.lines.map((line) => `${line.businessUnit.nameFa} ${pct(line.percentage)}`).join("، ")}</p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setTarget(item)}><Undo2 className="size-4" aria-hidden /> برگشت به کاربر</Button>
          </div>
        ))}
      </CardContent>
      {target && <ReturnDialog allocationId={target.id} employee={target.employee.nameFa} onClose={() => setTarget(null)} />}
    </Card>
  )
}

function RejectDialog({ row, onClose, onReject }: { row: Row; onClose: () => void; onReject: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState("")
  const mutation = useMutation({ mutationFn: () => onReject(reason.trim()) })
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>برگشت نسبت کاری {row.employee}</DialogTitle>
          <DialogDescription>علت را بنویسید تا فرد بداند چه چیزی را اصلاح کند. نسبت کاری برای اصلاح به او برمی‌گردد.</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); mutation.mutate() }}>
          <Field>
            <FieldLabel htmlFor="reject-reason">علت برگشت</FieldLabel>
            <Textarea id="reject-reason" value={reason} onChange={(event) => setReason(event.target.value)} autoFocus required />
          </Field>
          {mutation.error && <FieldError role="alert" className="mt-2">{messageFor(mutation.error)}</FieldError>}
          <DialogFooter className="mt-4">
            <Button type="submit" variant="destructive" disabled={!reason.trim() || mutation.isPending}>{mutation.isPending ? "در حال ارسال…" : "برگشت برای اصلاح"}</Button>
            <Button type="button" variant="ghost" onClick={onClose}>انصراف</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const pct = (value: string) => `${(Number(value) * 100).toLocaleString("fa-IR", { maximumFractionDigits: 2 })}٪`

/** Row total chip: proves the split sums to 100% without opening the detail page. */
function SumChip({ lines }: { lines: Split }) {
  const total = lines.reduce((sum, line) => sum + Number(line.percentage) * 100, 0)
  const ok = Math.abs(total - 100) < 0.005
  return (
    <span title="جمع سهم‌ها" className={cn("inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs tabular-nums", ok ? "bg-muted text-muted-foreground" : "bg-amber-100 text-amber-900")}>
      {!ok && <AlertTriangle className="size-3" aria-hidden />}
      جمع: {total.toLocaleString("fa-IR", { maximumFractionDigits: 2 })}٪
    </span>
  )
}
